import { useEffect, useMemo, useState } from "react";
import { Pager } from "../components/Pager";
import { Card, Chips, ErrorBanner, Pill, fmt, usePaged } from "../components/ui";
import { api, type Campaign, type EnrollmentJob } from "../lib/api";

type Filter = "drips" | "one_off" | "all";

/** Campaigns: one summary per campaign, its messages one click away.
 *
 *  Every card used to print every step - 74 rows for the Waterfall, 106 for
 *  No CC, 84 for CC No Sale - so the screen was 270 table rows before anyone
 *  found the Pause button. The steps are still here, behind "Show messages",
 *  and one-off campaigns, which multiply with every scheduled blast, have
 *  their own filter instead of pushing the drips down the page.
 */
/** Audiences being enrolled right now, and those of the last few hours. */
function Enrolling() {
  const [jobs, setJobs] = useState<EnrollmentJob[]>([]);
  const load = () => api.enrollmentJobs().then((r) => setJobs(r.jobs)).catch(() => undefined);
  useEffect(() => {
    load();
    const t = window.setInterval(load, 4000);
    return () => window.clearInterval(t);
  }, []);
  const live = jobs.filter((j) => j.status === "queued" || j.status === "running");
  if (!live.length) return null;
  return (
    <div className="banner stack" style={{ marginBottom: 12 }}>
      {live.map((j) => (
        <div key={j.id} className="row w" style={{ gap: 10, alignItems: "center" }}>
          <b>Enrolling into {j.campaign_name ?? `campaign ${j.campaign_id}`}</b>
          <span className="mono">
            {fmt.n(j.position)} of {fmt.n(j.matched)} ({Math.round(j.done_share * 100)}%)
          </span>
          <div style={{ flex: "1 1 160px", height: 6, background: "var(--line)", borderRadius: 3 }}>
            <div style={{ width: `${Math.round(j.done_share * 100)}%`, height: 6, borderRadius: 3,
                          background: "var(--acc)" }} />
          </div>
          <span className="dim" style={{ fontSize: 12 }}>
            by {j.created_by} - it carries on by itself; do not start it again
          </span>
          <button
            className="btn"
            onClick={() => {
              if (window.confirm("Stop enrolling the rest? Those already in stay in.")) {
                api.cancelEnrollment(j.id).then(load);
              }
            }}
          >
            Stop
          </button>
        </div>
      ))}
    </div>
  );
}

/** Where the people in a campaign are waiting: how many sit on each day. */
function Positions({ id }: { id: number }) {
  const [p, setP] = useState<Awaited<ReturnType<typeof api.campaignPositions>> | null>(null);
  useEffect(() => {
    let alive = true;
    api.campaignPositions(id).then((r) => alive && setP(r)).catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [id]);
  if (!p || !p.days.length) return null;
  const peak = Math.max(...p.days.map((d) => d.people));
  return (
    <Card title="Where people are waiting">
      <div className="dim" style={{ fontSize: 12.5, marginBottom: 8 }}>
        {fmt.n(p.active)} still going · {fmt.n(p.exited)} left · {fmt.n(p.finished)} finished. Each row is
        the day whose text they get next.
      </div>
      <div className="tw" style={{ maxHeight: 320, overflowY: "auto" }}>
        <table>
          <thead>
            <tr>
              <th>Day</th>
              <th>Message</th>
              <th style={{ textAlign: "right" }}>People</th>
              <th></th>
              <th style={{ textAlign: "right" }}>Due today</th>
              <th>Next text</th>
            </tr>
          </thead>
          <tbody>
            {p.days.map((d) => (
              <tr key={`${d.day_offset}-${d.step_index}`}>
                <td className="mono">Day {d.day_offset}</td>
                <td className="dim">{d.name ?? `Step ${d.step_index + 1}`}</td>
                <td className="mono" style={{ textAlign: "right" }}>
                  <b>{fmt.n(d.people)}</b>
                </td>
                <td style={{ width: 160 }}>
                  <div style={{ height: 6, borderRadius: 3, background: "var(--line)" }}>
                    <div style={{ width: `${Math.round((100 * d.people) / peak)}%`, height: 6,
                                  borderRadius: 3, background: "var(--acc)" }} />
                  </div>
                </td>
                <td className="mono dim" style={{ textAlign: "right" }}>{fmt.n(d.due_today)}</td>
                <td className="mono dim">{d.next_text ? fmt.when(d.next_text) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function Campaigns() {
  const [camps, setCamps] = useState<Campaign[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [phone, setPhone] = useState("");
  const [dry, setDry] = useState<Array<Record<string, unknown>> | null>(null);
  const [dryFor, setDryFor] = useState<number | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Filter>("drips");
  const [open, setOpen] = useState<Set<number>>(new Set());

  const load = () => api.campaigns().then(setCamps).catch((e) => setError(e.message));

  useEffect(() => {
    load();
  }, []);

  const isOneOff = (c: Campaign) => c.campaign_type === "one_off";
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return camps
      .filter(
        (c) =>
          (filter === "all" || (filter === "one_off" ? isOneOff(c) : !isOneOff(c))) &&
          (!needle || c.name.toLowerCase().includes(needle)),
      )
      // live first, then paused, then drafts; biggest first within each
      .sort(
        (a, b) =>
          rank(a.status) - rank(b.status) || b.active_enrollments - a.active_enrollments,
      );
  }, [camps, q, filter]);
  const paged = usePaged(filtered, [q, filter]);

  const toggle = (c: Campaign) => {
    const call = c.status === "paused" ? api.resumeCampaign(c.id) : api.pauseCampaign(c.id);
    call
      .then((r) => {
        if ("due_immediately" in r && r.due_immediately > 0) {
          setNote(
            `${r.due_immediately} messages came due while it was paused and will go out on the next tick, within a minute.`,
          );
        } else if ("enrollments_held" in r) {
          setNote(`Paused. ${r.enrollments_held} people are held where they are — nobody was exited.`);
        }
        return load();
      })
      .catch((e) => setError(e.message));
  };

  // Takes effect on the next text: the send-time check reads it (within a
  // minute), so nothing already queued slips past a switch turned on.
  const setSkipCustomers = (c: Campaign, on: boolean) => {
    if (
      !on &&
      !window.confirm(
        `Let "${c.name}" text customers? People who have bought will start getting its messages.`,
      )
    ) {
      return;
    }
    api
      .campaignSettings(c.id, { skip_customers: on })
      .then(() => {
        setNote(
          on
            ? `"${c.name}" will not text anyone who has bought. Customers already in it leave at their next message.`
            : `"${c.name}" can now text customers.`,
        );
        return load();
      })
      .catch((e) => setError(e.message));
  };

  // Hold from Day N: the long tail waits, the welcome keeps going.
  const [holdDay, setHoldDay] = useState<Record<number, string>>({});
  const [shift, setShift] = useState<Record<number, boolean>>({});

  const hold = (c: Campaign) => {
    const day = Number(holdDay[c.id] ?? 6);
    if (!Number.isInteger(day) || day < 1) {
      setError("Hold from Day 1 or later - Day 0 is the welcome, and holding it is what Pause is for.");
      return;
    }
    if (!window.confirm(
      `Hold "${c.name}" from Day ${day}?\n\nEveryone on Day ${day} or later waits where they are, and anyone who reaches Day ${day} waits there too. Days 0-${day - 1} and new people keep going. Replies, bookings and opt-outs still take people out.`,
    )) return;
    api
      .holdCampaign(c.id, day)
      .then((r) => {
        setNote(`Held from Day ${r.hold_from_day}: ${fmt.n(r.held)} people are waiting. Earlier days keep running.`);
        return load();
      })
      .catch((e) => setError(e.message));
  };

  const release = (c: Campaign) => {
    const moveOn = shift[c.id] ?? true;
    if (!window.confirm(
      moveOn
        ? `Release the hold on "${c.name}"?\n\n${fmt.n(c.held_enrollments ?? 0)} people carry on, each moved on by the days they waited, so they get their next text at their usual time.`
        : `Release the hold on "${c.name}" WITHOUT moving schedules on?\n\nEverything that came due while they waited goes out at once.`,
    )) return;
    api
      .releaseCampaign(c.id, moveOn)
      .then((r) => {
        setNote(
          `${fmt.n(r.resumed)} people carry on${r.shifted ? ", schedules moved on by the days they waited" : ""}.` +
            (r.due_immediately > 0 ? ` ${fmt.n(r.due_immediately)} are due now and go on the next tick.` : ""),
        );
        return load();
      })
      .catch((e) => setError(e.message));
  };

  const runDry = (id: number) => {
    setDryFor(id);
    api
      .dryRun(id, phone)
      .then((r) => {
        setDry(r as never);
        setError(null);
      })
      .catch((e) => {
        setDry(null);
        setError(e.message);
      });
  };

  const expand = (id: number) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <>
      <ErrorBanner error={error} />
      <Enrolling />
      {note && <div className="banner ok stack">{note}</div>}
      <div className="row w" style={{ marginBottom: 4 }}>
        <h2 style={{ fontSize: 19 }}>Campaigns</h2>
        <div className="sp" />
        <a className="btn p" href="/campaigns/new">
          New campaign
        </a>
      </div>

      {camps.length === 0 ? (
        <div className="empty">
          No campaigns yet. A campaign is a cadence — day 0, day 1, day 3 — that keeps going
          until someone replies.
        </div>
      ) : (
        <div className="row w" style={{ margin: "6px 0 12px" }}>
          <input
            className="f"
            style={{ width: 240 }}
            placeholder="Search by name"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Chips<Filter>
            value={filter}
            onChange={setFilter}
            options={[
              { key: "drips", label: "Drip campaigns", count: camps.filter((c) => !isOneOff(c)).length },
              { key: "one_off", label: "One-offs", count: camps.filter(isOneOff).length },
              { key: "all", label: "All", count: camps.length },
            ]}
          />
        </div>
      )}

      {camps.length > 0 && filtered.length === 0 && <div className="empty">No campaigns match.</div>}

      {paged.shown.map((c) => {
        const isOpen = open.has(c.id);
        const lastDay = c.steps.reduce((m, s) => Math.max(m, s.day_offset), 0);
        const on = c.steps.filter((s) => s.enabled).length;
        return (
          <Card
            key={c.id}
            title={
              <div className="row" style={{ gap: 9 }}>
                <h3>{c.name}</h3>
                <Pill tone={c.status === "active" ? "ok" : "nu"}>{c.status}</Pill>
                <Pill tone="nu">{c.campaign_type.replace(/_/g, "-")}</Pill>
                {c.skip_customers && <Pill tone="wa">skips customers</Pill>}
                {c.hold_from_day != null && <Pill tone="wa">held from day {c.hold_from_day}</Pill>}
              </div>
            }
            actions={
              <div className="row" style={{ gap: 8 }}>
                <span className="mono dim">{fmt.n(c.active_enrollments)} in it now</span>
                <label
                  className="row"
                  style={{ gap: 5, fontSize: 12.5, cursor: "pointer" }}
                  title="Nobody who has bought gets this campaign's texts. Blasts are not affected."
                >
                  <input
                    type="checkbox"
                    checked={!!c.skip_customers}
                    onChange={(e) => setSkipCustomers(c, e.target.checked)}
                  />
                  Never text customers
                </label>
                {(c.status === "active" || c.status === "paused") && (
                  <button
                    className={`btn s${c.status === "paused" ? " p" : ""}`}
                    onClick={() => toggle(c)}
                  >
                    {c.status === "paused" ? "Resume" : "Pause"}
                  </button>
                )}
                <a className="btn s" href={`/campaigns/${c.id}/edit`}>
                  Edit
                </a>
              </div>
            }
          >
            <div className="row w">
              <span className="dim" style={{ fontSize: 12.5 }}>
                {c.steps.length === 0
                  ? "No steps configured."
                  : `${fmt.n(c.steps.length)} message${c.steps.length === 1 ? "" : "s"} over ${
                      lastDay + 1
                    } day${lastDay === 0 ? "" : "s"}${
                      on < c.steps.length ? ` · ${fmt.n(c.steps.length - on)} switched off` : ""
                    }`}
              </span>
              <div className="sp" />
              {c.steps.length > 0 && (
                <button className="btn s" onClick={() => expand(c.id)}>
                  {isOpen ? "Hide messages" : "Show messages"}
                </button>
              )}
            </div>

            {c.status === "active" && lastDay > 0 && (
              <div className="row w" style={{ marginTop: 10, gap: 8, fontSize: 12.5 }}>
                {c.hold_from_day != null || (c.held_enrollments ?? 0) > 0 ? (
                  <>
                    <span>
                      <b>Held from Day {c.hold_from_day ?? "?"}</b>
                      {c.hold_started_at && <span className="dim"> since {fmt.when(c.hold_started_at)}</span>}
                      {" · "}
                      <span className="mono">{fmt.n(c.held_enrollments ?? 0)}</span> people waiting. Earlier days
                      and new people keep running.
                    </span>
                    <div className="sp" />
                    <label className="row" style={{ gap: 5, cursor: "pointer" }}
                           title="Each person's next text moves on by the days they waited, so nothing piles up">
                      <input
                        type="checkbox"
                        checked={shift[c.id] ?? true}
                        onChange={(e) => setShift((s) => ({ ...s, [c.id]: e.target.checked }))}
                      />
                      move schedules on by the days held
                    </label>
                    <button className="btn s p" onClick={() => release(c)}>
                      Release hold
                    </button>
                  </>
                ) : (
                  <>
                    <span className="dim">Hold everyone from Day</span>
                    <input
                      className="f"
                      type="number"
                      min={1}
                      max={lastDay}
                      style={{ width: 64 }}
                      value={holdDay[c.id] ?? "6"}
                      onChange={(e) => setHoldDay((h) => ({ ...h, [c.id]: e.target.value }))}
                    />
                    <span className="dim">on - earlier days and new sign-ups keep going</span>
                    <button className="btn s" onClick={() => hold(c)}>
                      Hold
                    </button>
                  </>
                )}
              </div>
            )}

            {isOpen && <Positions id={c.id} />}
            {isOpen && (
              <>
                <div className="tw" style={{ marginTop: 12, maxHeight: 420, overflowY: "auto" }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Step</th>
                        <th>Day</th>
                        <th>Name</th>
                        <th>Waits</th>
                        <th>On</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.steps.map((s) => (
                        <tr key={s.id} style={{ opacity: s.enabled ? 1 : 0.45 }}>
                          <td className="mono">{s.step_index}</td>
                          <td className="mono">{s.day_offset}</td>
                          <td>{s.name ?? "—"}</td>
                          <td className="mono dim">
                            {s.delay_minutes >= 1440
                              ? `${Math.round(s.delay_minutes / 1440)} day(s)`
                              : `${Math.round(s.delay_minutes / 60)} hour(s)`}
                          </td>
                          <td>
                            <Pill tone={s.enabled ? "ok" : "nu"}>{s.enabled ? "on" : "off"}</Pill>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="row w" style={{ marginTop: 12 }}>
                  <input
                    className="f"
                    style={{ width: 200 }}
                    placeholder="+1 407 555 0182"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                  <button className="btn s" disabled={!phone} onClick={() => runDry(c.id)}>
                    Try it on this person
                  </button>
                  <span className="dim" style={{ fontSize: 12 }}>
                    Shows exactly what they would receive, and what would be skipped or held.
                  </span>
                </div>

                {dryFor === c.id && dry && (
                  <div style={{ marginTop: 12 }}>
                    {dry.map((r, i) => (
                      <div className="brk" key={i} style={{ fontFamily: "var(--sans)", padding: "7px 0" }}>
                        <span className="mono dim">day {String(r.day_offset)}</span>
                        <span>
                          <Pill
                            tone={r.outcome === "would_send" ? "ok" : r.outcome === "held" ? "wa" : "nu"}
                          >
                            {String(r.outcome).replace(/_/g, " ")}
                          </Pill>{" "}
                          {r.detail ? <span className="dim">{String(r.detail)}</span> : null}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </Card>
        );
      })}

      {filtered.length > paged.perPage && (
        <Pager
          page={paged.page}
          pages={paged.pages}
          total={paged.total}
          perPage={paged.perPage}
          onPage={paged.setPage}
          onPerPage={paged.setPerPage}
        />
      )}
    </>
  );
}

function rank(status: string) {
  return status === "active" ? 0 : status === "paused" ? 1 : 2;
}
